import React, { useState, FormEvent } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { httpsCallable } from '@/lib/callableProxy';
import { functions as firebaseFunctions } from '@/config/firebase';
import { toast as sonnerToast } from "sonner";
import { Loader2, Mail } from 'lucide-react';

interface InviteMemberDialogProps {
  isOpen: boolean;
  onOpenChange: (isOpen: boolean) => void;
  workspaceId: string;
  onInviteSent: () => void;
}

export const InviteMemberDialog: React.FC<InviteMemberDialogProps> = ({ isOpen, onOpenChange, workspaceId, onInviteSent }) => {
  const [email, setEmail] = useState('');
  const [role, setRole] = useState<'admin' | 'editor' | 'viewer'>('editor');
  const [isSending, setIsSending] = useState(false);

  const handleInvite = async (e: FormEvent) => {
    e.preventDefault();
    if (!email || !email.includes('@')) {
        sonnerToast.error("Please enter a valid email address.");
        return;
    }
    setIsSending(true);
    const toastId = sonnerToast.loading("Sending invitation...");

    try {
      const sendInviteFn = httpsCallable(firebaseFunctions, 'sendWorkspaceInvite');
      await sendInviteFn({ workspaceId, email, role });
      
      sonnerToast.success(`Invitation sent to ${email}!`, { id: toastId });
      onInviteSent();
      onOpenChange(false);
      setEmail('');
      setRole('editor');
    } catch (error: any) {
      // Handle specific error codes for better UX
      if (error.message.includes('limit')) {
          sonnerToast.error("Plan Limit Reached: Please upgrade your subscription to invite more members.", { id: toastId, duration: 5000 });
      } else if (error.message.includes('already a member')) {
          sonnerToast.error("User is already a member of this workspace.", { id: toastId });
      } else {
          sonnerToast.error(`Failed to send invite: ${error.message}`, { id: toastId });
      }
    } finally {
      setIsSending(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-2xl border-slate-200 bg-white">
        <DialogHeader>
          <DialogTitle className="tracking-[-0.02em] text-slate-950">Invite Team Member</DialogTitle>
          <DialogDescription className="text-slate-600">
            Send an email invitation to join this workspace. They will have access based on the role you select.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleInvite} className="space-y-4 py-4">
          <div className="space-y-2">
            <Label htmlFor="invite-email">Email Address</Label>
            <div className="relative">
                <Mail className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                <Input
                id="invite-email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="colleague@company.com"
                className="rounded-xl border-slate-200 bg-white pl-9 focus-visible:ring-slate-300"
                required
                disabled={isSending}
                />
            </div>
          </div>
          <div className="space-y-2">
            <Label htmlFor="invite-role">Role</Label>
            <Select value={role} onValueChange={(val: any) => setRole(val)} disabled={isSending}>
              <SelectTrigger id="invite-role" className="rounded-xl border-slate-200 bg-white">
                <SelectValue placeholder="Select a role" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="admin">
                    <div className="flex flex-col text-left">
                        <span className="font-medium">Admin</span>
                        <span className="text-xs text-slate-500">Can manage members, billing, and settings.</span>
                    </div>
                </SelectItem>
                <SelectItem value="editor">
                    <div className="flex flex-col text-left">
                        <span className="font-medium">Editor</span>
                        <span className="text-xs text-slate-500">Can upload, process, and sync invoices.</span>
                    </div>
                </SelectItem>
                <SelectItem value="viewer">
                    <div className="flex flex-col text-left">
                        <span className="font-medium">Viewer</span>
                        <span className="text-xs text-slate-500">Read-only access to invoices and reports.</span>
                    </div>
                </SelectItem>
              </SelectContent>
            </Select>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" className="rounded-xl border-slate-200 bg-white hover:bg-slate-50" onClick={() => onOpenChange(false)} disabled={isSending}>Cancel</Button>
            <Button type="submit" className="rounded-xl bg-slate-900 text-white hover:bg-slate-800" disabled={isSending}>
              {isSending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Send Invite
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
